import { Component, EventEmitter, Inject, Input, OnInit, Output, OnDestroy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service'
import { finalize } from 'rxjs/operators'
import { ReassignTaskModalComponent } from '../reassign-task-modal/reassign-task-modal.component'
import { ConfirmationDialogComponent } from 'src/app/leader-toolkit/components/confirmation-dialog/confirmation-dialog.component'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { Editor, Toolbar } from 'ngx-editor'
import { richTextToHtml } from 'src/app/leader-toolkit/utils/rich-text.util'
import moment from 'moment'

@Component({
   selector: 'app-view-task-modal',
   standalone: false,
   templateUrl: './view-task-modal.component.html',
   styleUrls: ['./view-task-modal.component.scss'],
})
export class ViewTaskModalComponent implements OnInit, OnDestroy {
   taskId: any
   taskData: any
   taskTagDataUserlist: any
   isLoading = true
   newCommentText: string='';
   taskComments: any
   userNameData: any
   currentUserName: string = '';
   canCompleteTask: boolean = false;
   multipulTaskData: any[] = [];
   multiTask: boolean = false;
   editor: Editor = new Editor();
   editingComment: any = null;
   editText: string = '';
   editEditor: Editor = new Editor();
   deleteConfirmComment: any = null;
   quarterList: any[] = [];
   selectedQuarter: string = '';
   constructor(
      private dialog: MatDialog,
      public leaderService: LeaderService,
      @Inject(MAT_DIALOG_DATA) private dialogdata: any, private authMgr: AuthManager,
      private dialogRef: MatDialogRef<ViewTaskModalComponent>
   ) {

   }

   ngOnInit(): void {
      if (this.dialogdata.page === 'tasks') {
         this.taskId = this.dialogdata.taskId;
      }
      else {
         this.taskId = this.dialogdata.taskId[0].taskId;
         this.multipulTaskData = this.dialogdata.taskId;
         this.multiTask = true;
      }
      // Seed from the opening page so the modal opens on the same quarter the user was
      // looking at; getQtrList() then fills the dropdown (and backfills the current
      // quarter for any caller that didn't pass one).
      this.selectedQuarter = (this.dialogdata.quarter || '').toString().trim();
      this.loadQuarters();

   }

   /**
    * Fetches the dropdown and performs the first task fetch. Resolving the quarter
    * before the first GET avoids landing on the current quarter for a beat and then
    * re-fetching — the same pattern hospital-ops-dashboard uses (loadQuarters/applyQuarter).
    */
   loadQuarters(): void {
      this.leaderService.getQtrList().subscribe({
         next: (res: any) => {
            this.quarterList = Array.isArray(res?.data) ? res.data : [];
            if (!this.selectedQuarter || this.selectedQuarter.toLowerCase() === 'default') {
               const currentQuarter = this.quarterList.find((q: any) => q?.currentQuarter);
               this.selectedQuarter = currentQuarter?.quarterName || '';
            }
            this.taskDetailsData(this.taskId, this.selectedQuarter);
         },
         error: () => this.taskDetailsData(this.taskId, this.selectedQuarter)
      });
   }

   /** Quarter name to send to the API; '' means "let the server resolve the current quarter". */
   getResolvedQuarter(): string {
      const q = (this.selectedQuarter || '').toString().trim();
      return (q && q.toLowerCase() !== 'default') ? q : '';
   }

   onQuarterChange(quarter: string): void {
      this.selectedQuarter = (quarter ?? '').toString().trim();
      this.taskDetailsData(this.taskId, this.selectedQuarter);
   }

   ngOnDestroy(): void {
      this.editor.destroy();
      this.editEditor.destroy();
   }

   get currentTaskIndex(): number {
      if (!this.multiTask || !this.multipulTaskData?.length) {
         return 0;
      }
      const index = this.multipulTaskData.findIndex((t) => t?.taskId === this.taskId);
      return index >= 0 ? index + 1 : 0;
   }

   get totalTasks(): number {
      return this.multipulTaskData?.length || 0;
   }

   get canGoPrev(): boolean {
      return this.currentTaskIndex > 1;
   }

   get canGoNext(): boolean {
      return this.currentTaskIndex < this.totalTasks;
   }

   getCurrentUserData() {
      this.authMgr.getCurrentUser().subscribe((user) => {
         this.userNameData = user;
         this.currentUserName = this.userNameData?.displayName || this.userNameData?.name || '';
         this.checkCanCompleteTask();
      });
   }

   taskDetailsData(taskId: any, quarter?: string) {
      this.isLoading = true
      this.leaderService.getTaskDetailsById(taskId, quarter ?? this.getResolvedQuarter())
         .pipe(finalize(() => { this.isLoading = false }))
         .subscribe((res: any) => {
            this.taskData = res.data || []
            this.descriptionHtml = richTextToHtml(this.taskData?.taskDescription);
            this.getCurrentUserData();
            this.getCommentsByTaskId(taskId);
            this.taskTagUserlistData(taskId);
            this.checkCanCompleteTask();
         });
   }

   goToPrevTask() {
      if (!this.canGoPrev) {
         return;
      }
      const newIndex = this.currentTaskIndex - 2;
      const prevTask = this.multipulTaskData[newIndex];
      if (prevTask?.taskId) {
         this.taskId = prevTask.taskId;
         this.taskDetailsData(this.taskId);
      }
   }

   goToNextTask() {
      if (!this.canGoNext) {
         return;
      }
      const newIndex = this.currentTaskIndex;
      const nextTask = this.multipulTaskData[newIndex];
      if (nextTask?.taskId) {
         this.taskId = nextTask.taskId;
         this.taskDetailsData(this.taskId);
      }
   }

   taskTagUserlistData(taskId: any) {
      this.isLoading = true
      this.leaderService.gettasktaguserlist(taskId)
         .pipe(finalize(() => { this.isLoading = false }))
         .subscribe((res: any) => {
            this.taskTagDataUserlist = res.data || []
         });
   }

   checkCanCompleteTask() {
      if (this.taskData && this.currentUserName) {
         const isCreator = this.taskData?.createdByName === this.currentUserName;
         const isAssignee = this.taskData?.assignedTo === this.currentUserName;
         this.canCompleteTask = isCreator || isAssignee;
      }
   }

   getCommentsByTaskId(taskId: any) {
      this.isLoading = true
      this.leaderService.getcommentbytaskid(taskId)
         .pipe(finalize(() => { this.isLoading = false }))
         .subscribe((res: any) => {
            this.taskComments = res.data || []
         });
   }

   postCommentByTaskId() {
      const commentData = {
         taskId: this.taskId,
         commentData: this.newCommentText,
      };
      this.isLoading = true
      this.leaderService.createCommentByTask(commentData)
         .pipe(finalize(() => { this.isLoading = false }))
         .subscribe((res: any) => {
            this.leaderService.showNotification('Comment added successfully!', false);
            this.newCommentText = '';
            this.getCommentsByTaskId(this.taskId); // Refresh comments
         });
   }

   startEditComment(comment: any) {
      this.editingComment = comment;
      this.editText = comment.commentData || '';
   }

   cancelEditComment() {
      this.editingComment = null;
      this.editText = '';
   }

   saveEditComment() {
      if (!this.editText.trim()) return;
      const data = {
         commentId: this.editingComment.commentId,
         taskId: this.taskId,
         commentData: this.editText
      };
      this.leaderService.updateCommentByTask(data).subscribe({
         next: (res: any) => {
            this.leaderService.showNotification('Comment updated successfully!', false);
            this.editingComment = null;
            this.editText = '';
            this.getCommentsByTaskId(this.taskId);
         },
         error: () => {
            this.leaderService.showNotification('Failed to update comment', true);
         }
      });
   }

   confirmDeleteComment(comment: any) {
      this.deleteConfirmComment = comment;
   }

   cancelDeleteComment() {
      this.deleteConfirmComment = null;
   }

   deleteComment() {
      if (!this.deleteConfirmComment) return;
      const data = {
         commentId: this.deleteConfirmComment.commentId,
         taskId: this.taskId
      };
      this.leaderService.deleteCommentByTask(data).subscribe({
         next: (res: any) => {
            this.leaderService.showNotification('Comment deleted successfully!', false);
            this.deleteConfirmComment = null;
            this.getCommentsByTaskId(this.taskId);
         },
         error: () => {
            this.leaderService.showNotification('Failed to delete comment', true);
         }
      });
   }

   isOwnComment(comment: any): boolean {
      return comment.createdBy === this.currentUserName;
   }

   /**
    * Description is rich text (an HTML string). Normalising on load also covers
    * records saved while the editor was briefly emitting its document object —
    * those are converted to HTML here instead of being printed as raw JSON.
    */
   descriptionHtml: string | null = null;


       toolbar: Toolbar = [
         ['bold', 'italic'],
         ['underline', 'strike'],
         ['code', 'blockquote'],
         ['ordered_list', 'bullet_list'],
         [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
         ['text_color', 'background_color'],
         ['align_left', 'align_center', 'align_right', 'align_justify'],
       ];
   


   onClose() {
      this.dialogRef.close();
   }

   formatCommentDate(commentDate: any): string {
      if (!commentDate) {
         return '—';
      }
      try {
         // Parse as UTC first, then convert to local timezone
         const date = moment.utc(commentDate).local();
         return date.format('MMM DD, YYYY hh:mm a');
      } catch (error) {
         console.error('Error formatting date:', error);
         return commentDate;
      }
   }

   openReassignDialog() {
      const dialogRef = this.dialog.open(ReassignTaskModalComponent, {
         width: '450px',
         data: {
            taskId: this.taskId,
            currentAssignee: this.taskData?.assignedTo,
            taskTagDataUserlist: this.taskTagDataUserlist,
         },
         disableClose: false,
         panelClass: 'reassign-dialog'
      });

      dialogRef.afterClosed().subscribe(result => {
         if (result?.success) {
            this.leaderService.showNotification('Task New Assignee added successfully!', false);
            this.taskTagUserlistData(this.taskId);
         }
      });
   }

   openCompleteDialog() {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
         width: '450px',
         data: {
            title: 'Complete Task',
            message: 'Are you sure you want to mark this task as complete? This action will update the task status.',
            confirmButtonText: 'Complete',
            cancelButtonText: 'Cancel'
         },
         disableClose: false
      });

      dialogRef.afterClosed().subscribe(result => {
         if (result) {
            this.completeTask();
         }
      });
   }

   completeTask() {
      this.isLoading = true;
      let data = { taskId: this.taskId };
      this.leaderService.completeTask(data)
         .pipe(finalize(() => { this.isLoading = false; }))
         .subscribe({
            next: (res: any) => {
               this.leaderService.showNotification('Task completed successfully!', false);
               this.leaderService.clearTaskListCache();
               this.dialogRef.close({ success: true });
            },
            error: () => {
               this.leaderService.showNotification('Failed to complete task', true);
            }
         });
   }
}
